import { useState, type FormEvent } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useFeature, PRINT_ORDERING } from '../contexts/ConfigContext';
import { usePageTitle } from '../hooks/usePageTitle';
import styles from './LoginPage.module.css';

const ERROR_MESSAGES: Record<string, string> = {
  link_expired: 'That link has expired. Please request a new one.',
  // Sent when neither Shopify nor any order knows the signed-in email.
  no_orders:
    "We couldn't find a Chemical Dependency Lab account for that email. Please use the email you shop with at CDL.",
  invalid_state: 'Something went wrong. Please try again.',
  service_error: 'Login is temporarily unavailable. Please try again later.',
};

export function LoginPage() {
  usePageTitle('Sign in');
  // Gated so the page never advertises prints if the flag is off. Anonymous
  // callers resolve the flag too, so this reads correctly before sign-in.
  const printEnabled = useFeature(PRINT_ORDERING);
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const errorParam = searchParams.get('error');
  const queryError = errorParam ? ERROR_MESSAGES[errorParam] ?? null : null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setSuccessMessage(null);
    setSubmitting(true);

    try {
      const res = await fetch('/api/v1/auth/magic-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (res.status === 429) {
        setFormError('Please wait before requesting another link.');
      } else {
        setSuccessMessage(
          "Check your email for a login link. The link is valid for 15 minutes. If you haven't received it, check your spam folder.",
        );
      }
    } catch {
      setFormError('Login is temporarily unavailable. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  }

  function handleShopifyLogin() {
    window.location.href = '/api/v1/auth/shopify/login';
  }

  return (
    <main className={styles.page}>
      <h1 className={styles.heading}>Log in to Loupe</h1>
      <p className={styles.subtitle}>
        {printEnabled
          ? "Log in to see your developed film, download your scans and order prints. Enter your email and we'll send you a login link."
          : "Log in to see your developed film and download your scans. Enter your email and we'll send you a login link."}
      </p>

      {queryError && (
        <p role="alert" className={styles.error}>
          {queryError}
        </p>
      )}

      {formError && (
        <p role="alert" className={styles.error}>
          {formError}
        </p>
      )}

      {successMessage && (
        <p role="status" className={styles.success}>
          {successMessage}
        </p>
      )}

      <form onSubmit={handleSubmit} className={styles.form}>
        <label htmlFor="login-email" className={styles.label}>
          Email address
        </label>
        <input
          id="login-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={submitting}
          className={styles.input}
          placeholder="you@example.com"
          autoComplete="email"
        />
        <button type="submit" disabled={submitting} className={styles.submitBtn}>
          {submitting ? 'Sending...' : 'Send login link'}
        </button>
      </form>

      <div className={styles.divider}>
        <span className={styles.dividerLine} />
        <span>or</span>
        <span className={styles.dividerLine} />
      </div>

      <button
        type="button"
        onClick={handleShopifyLogin}
        className={styles.shopifyBtn}
      >
        Sign in with Shopify
      </button>

      <p className={styles.cookieNote}>
        Signing in sets a single strictly-necessary cookie to keep you logged in.
        See our <Link to="/cookie-policy">Cookie Policy</Link>.
      </p>
    </main>
  );
}
